import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { DateUtils } from '@app/api/core/utils/date.util';
import { deepCopy } from '@app/api/core/utils/deep-copy.utils';
import { compareBeratungChange } from '@app/shared/comparator/gen/beratungchange-comparator';
import { AbstractReactiveDialogComponent } from '@app/shared/components/dialog/reactive-dialog/base/abstract-reactive-dialog-component';
import { ReactiveDialogContentComponent } from '@app/shared/components/dialog/reactive-dialog/base/reactive-dialog-content/reactive-dialog-content.component';
import { FieldPropertyValidator } from '@app/shared/components/validators/fieldproperty-validator';
import { DataService } from '@app/shared/services/data/data.service';
import { Message, MessageType } from '@app/shared/services/ui/message/message';
import { MessageService } from '@app/shared/services/ui/message/message.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BeratungChange, DateString, Inkasso, Payload, Person, Polizze, PolizzeLogik, Vke } from '@o30/entities';
import { EMPTY, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { BasicDatepickerComponent } from '../../form/basic-datepicker/basic-datepicker.component';
import { BasicInputComponent } from '../../form/basic-input/basic-input.component';
import { InkassoComponent } from '../../nachlauf/inkasso/inkasso.component';

interface BeratungChangeContext {
  polizzeId: Polizze['id'];
  vkeId: Vke['id'];
  ersterKundeId: Person['id'];
  // isLeben: boolean;
  isKonvertierung: boolean;
}

@UntilDestroy()
@Component({
  selector:    'app-beratungchange-dialog',
  templateUrl: './polizzechange-dialog.component.html',
  styleUrls:   ['./polizzechange-dialog.component.scss'],
  standalone:  true,
  imports:     [
    ReactiveDialogContentComponent,
    BasicDatepickerComponent,
    FormsModule,
    ReactiveFormsModule,
    BasicInputComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    InkassoComponent,
    MatButton
  ]
})
export class PolizzechangeDialogComponent extends AbstractReactiveDialogComponent<BeratungChange, BeratungChangeContext> implements OnInit {

  private dataService = inject(DataService);
  private messageService = inject(MessageService);

  formGroup: UntypedFormGroup;
  beratungChange: BeratungChange;
  polizzeId: Polizze['id'];
  vkeId: Vke['id'];
  ersterKundeId: Person['id'];
  isKonvertierung: boolean;
  logik: PolizzeLogik;

  typvar: 'beratung' | 'antrag';

  inkasso: Inkasso;

  locked = false;

  public constructor() {
    super();
  }

  ngOnInit() {
    this.beratungChange = this.dialogInputData.data;
    const context = this.dialogInputData.context as BeratungChangeContext;
    this.polizzeId = context.polizzeId;
    this.vkeId = context.vkeId;
    this.ersterKundeId = context.ersterKundeId;
    this.isKonvertierung = context.isKonvertierung;
    this.inkasso = deepCopy(this.beratungChange.inkasso);

    this.dataService.getPolizzeLogik(this.polizzeId)
      .pipe(untilDestroyed(this), take(1), map(_payload => _payload.data))
      .subscribe(_logik => this.logik = _logik);

    this.initForm();
  }

  private initForm() {
    const fcConfig = {};

    for (const key of [
      'beginndatum',
      'ablaufdatum',
      'versicherungsbestaetigung'
    ]) {
      if (this.beratungChange[key]) {
        const value = this.beratungChange[key].hasOwnProperty('value') ? this.beratungChange[key]['value'] : undefined;
        fcConfig[key] = new UntypedFormControl(
          value
          , {
            validators: [FieldPropertyValidator([this.beratungChange[key]] as any)]
          });
      }
    }
    this.formGroup = new UntypedFormGroup(fcConfig);
    this.getFormControl('beginndatum')
      .valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(this.onBeginndatumChange.bind(this));

    this.getFormControl('ablaufdatum')
      .valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(this.onAblaufDatumChange.bind(this));
  }

  public getFormControl(key: keyof BeratungChange) {
    if (!this.formGroup?.get(key as string)) {
      console.warn('formcontrol is undefined: ', key);
    }
    return this.formGroup ? this.formGroup.get(key as string) : undefined;
  }

  private getFormValue(key: keyof BeratungChange) {
    const fc = this.getFormControl(key);
    return fc ? fc.value : undefined;
  }

  private wrapFieldProperty(value: number | string | DateString) {
    return {value};
  }

  private wrapFieldPropertyValue(key: keyof BeratungChange) {
    return this.wrapFieldProperty(this.getFormValue(key));
  }

  onSaveClick() {
    const ablaufDatum = this.getFormControl('ablaufdatum');
    const beginnDatum = this.getFormControl('beginndatum');

    if (this.logik && !this.logik.fixedLaufzeit) {
      if (beginnDatum?.value && ablaufDatum?.value) {
        if (DateUtils.moment(ablaufDatum.value)
          .isBefore(DateUtils.moment(beginnDatum.value))) {
          beginnDatum.setErrors({ablaufAfterBegin: true});
          ablaufDatum.setErrors({ablaufAfterBegin: true});
          return;
        }
        const diffInMonths = DateUtils.diffInMonths(ablaufDatum.value, beginnDatum.value, true);
        if (this.logik.minLaufzeit && diffInMonths < (this.logik.minLaufzeit * 12)) {
          beginnDatum.setErrors({minLaufzeit: {years: this.logik.minLaufzeit}});
          ablaufDatum.setErrors({minLaufzeit: {years: this.logik.minLaufzeit}});
          return;
        }
        if (this.logik.maxLaufzeit && diffInMonths > (this.logik.maxLaufzeit * 12)) {
          beginnDatum.setErrors({maxLaufzeit: {years: this.logik.maxLaufzeit}});
          ablaufDatum.setErrors({maxLaufzeit: {years: this.logik.maxLaufzeit}});
          return;
        }
      }
    }

    // check for invalid instead of !valid
    // https://github.com/angular/angular/issues/39754
    if (this.formGroup.invalid) {
      return;
    }

    const data = {} as BeratungChange;
    [
      'ablaufdatum',
      'beginndatum',
      'versicherungsbestaetigung'
    ].forEach(_key => data[_key] = this.wrapFieldPropertyValue(_key as keyof BeratungChange));
    data.inkasso = this.inkasso;

    const diff = compareBeratungChange(this.beratungChange, data) as BeratungChange;

    if (!diff) {
      this.messageService.showSnackBar({
        text: 'Keine Änderungen vorgenommen.',
        type: MessageType.WARNING
      } as Message);
      this.close();
      return;
    }

    this.putChangeFunction(this.polizzeId, diff)
      .pipe(
        untilDestroyed(this),
        take(1),
        switchMap(_payload => this.messageService.handlePayloadReturnSuccess(_payload) ? of(_payload) : EMPTY)
      )
      .subscribe(_payload => {
        const type = MessageType.INFO;
        const icon = 'info';
        const text = _payload?.businessErrors?.infos?.[0]?.text || `Änderungen gespeichert.`;
        this.messageService.showSnackBar({
          type,
          icon,
          text
        });
        this.close();
      });
  }

  get putChangeFunction(): (pid: number, bc: BeratungChange) => Observable<Payload<number>> {
    return this.dataService.putBeratungChange.bind(this.dataService);
  }

  get beginndatum() {
    return this.getFormControl('beginndatum').value;
  }

  // TODO dry
  private updateAblaufdatum(): void {
    if (this.logik.hauptfaelligkeit && this.isKonvertierung) {
      // hauptfaelligkeit
      const beginmoment = DateUtils.moment(this.beginndatum);

      const minLaufzeit = this.logik.minLaufzeit || 1;
      if (!!beginmoment && !!beginmoment.get('year') && beginmoment.get('year') > 1000) {
        const ablaufmoment = DateUtils.moment(this.beginndatum)
          .add(minLaufzeit, 'year');

        const vormoment = DateUtils.moment(this.logik.ablaufdatumVorpolizze);
        ablaufmoment.set({
          'month': vormoment.get('month'),
          'date':  vormoment.get('date')
        });
        if (beginmoment.diff(ablaufmoment, 'day') < DateUtils.DAYS_PER_YEAR * minLaufzeit) {
          ablaufmoment.add(1, 'year');
        }
        this.getFormControl('ablaufdatum')
          .setValue(DateUtils.format(ablaufmoment));
      }
    } else if (this.logik.fixedLaufzeit && this.beginndatum) {
      // fixed
      const ablaufdatum = DateUtils.addYears(this.beginndatum, this.logik.fixedLaufzeit);
      this.getFormControl('ablaufdatum')
        .setValue(ablaufdatum);
    }
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--events--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  onBeginndatumChange(): void {
    this.updateAblaufdatum();
    this.validateControl('beginndatum');
    this.validateControl('ablaufdatum');
  }

  onAblaufDatumChange(): void {
    this.validateControl('beginndatum');
  }

  private validateControl(key: keyof BeratungChange): void {
    this.getFormControl(key)
      .updateValueAndValidity();
  }
}
